import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import DropdownInput from "../../../components/DropdownInput";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {PayablBanksResponse} from "../../../types/Responses/WithdrawsResponse";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import {APILink} from "../../../types/APILink";

interface PayablFormProps {
    broker: number,
    clientTransactionID?: number
}

const PayablForm = ({broker, clientTransactionID}: PayablFormProps) => {
    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [method, setMethod] = useState<string>();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [phone, setPhone] = useState<string>();
    const [accountType, setAccountType] = useState<string>();
    const [accountNumber, setAccountNumber] = useState<string>();
    const [documentID, setDocumentID] = useState<string>();
    const [documentType, setDocumentType] = useState<string>();
    const [bank, setBank] = useState<number>();
    const [banks, setBanks] = useState<PayablBanksResponse>();
    const [nationalID, setNationalID] = useState<string>();

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const dispatch = useDispatch();
    const methods: FooterListItem[] = [
        {name: 'SPEI', code: 'spei'},
        {name: 'OXXO', code: 'oxxo'},
        {name: 'PIX', code: 'pix'},
    ]
    const allowedMethodData = [
        {code: 'pix', currencies: ['BRL']},
        {code: 'oxxo', currencies: ['MXN']},
        {code: 'spei', currencies: ['MXN']},
    ];

    const clearData = () => {
        setInProgress(false);
        setMethod('');
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setAccountNumber('');
        setDocumentID('');
        setDocumentType('');
        setAccountType('');
        setBank(0);
        setPhone('');
        setNationalID('');
        setValidated(false)
    }

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.first_name);
            setLastName(response.data.last_name);
            setEmail(response.data.email);
            if (response.data.request && response.data.request.method) {
                setMethod(response.data.request.method.toLowerCase());
            }
            if (response.data.request && response.data.request.phone) {
                setPhone(response.data.request.phone)
            }
            if (response.data.request && response.data.request.account_type) {
                setAccountType(response.data.request.account_type)
            }
            if (response.data.request && response.data.request.document_type) {
                setDocumentType(response.data.request.document_type)
            }
            if (response.data.request && response.data.request.document_id) {
                setDocumentID(response.data.request.document_id)
            }
            if (response.data.request && response.data.request.nationalid) {
                setNationalID(response.data.request.nationalid)
            }
            if (response.data.request && response.data.request.nationalid) {
                setNationalID(response.data.request.nationalid)
            }
            if (response.data.request && response.data.request.bank_code) {
                setBank(response.data.request.bank_code)
            }
            if (response.data.request && response.data.request.bankname) {
                setBank(response.data.request.bankname)
            }
            if (response.data.transaction_data) {
                setAccountNumber(response.data.transaction_data.accountnumber)
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [toast, handleError]);

    const getBanks = useCallback(() => {
        API.getPayablBanks().then((response: AxiosResponse) => {
            setBanks(response.data);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYABL_BANKS}`, toast, handleError));
    }, [toast, handleError]);

    const changeMethod = useCallback((s: string | number) => {
        clearData();
        setMethod(typeof s !== "string" ? s.toString() : s);
    }, [setMethod, clearData]);

    const changeDocumentType = useCallback((s: string | number) => {
        setDocumentType(typeof s !== "string" ? s.toString() : s);
        setAccountType(undefined)
    }, [setAccountType]);

    const changeAccountType = useCallback((s: string | number) => {
        setAccountType(typeof s !== "string" ? s.toString() : s);
    }, [setAccountType]);

    const changeBank = useCallback((s: string | number) => {
        setBank(typeof s !== "string" ? s : parseInt(s));
    }, [setBank]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeDocumentID = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setDocumentID(e.currentTarget.value)
        }
    }, [setValidated, setDocumentID]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeNationalID = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setNationalID(e.currentTarget.value)
        }
    }, [setValidated, setNationalID]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changePhone = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setPhone(e.currentTarget.value)
        }
    }, [setValidated, setPhone]);

    const changeAccountNumber = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountNumber(e.currentTarget.value)
        }
    }, [setValidated, setAccountNumber]);

    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const detectAccountRegex = useCallback((inputType: string) => {
        let accountRegex = '';
        switch (inputType.toLowerCase()) {
            case 'cpf':
                accountRegex = '^\\d{11}$';
                break;
            case 'cnpj':
                accountRegex = '^\\d{14}$';
                break;
            case 'email':
                accountRegex = emailRegex;
                break;
            case 'phone':
                accountRegex = '^([+55]{3})(\\(?[0-9]{2}\\)?)? ?([0-9]{4,5})-?([0-9]{4})$';
                break;
            case 'evp':
                accountRegex = '^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$';
                break;
            default:
                accountRegex = '^.{10,}$';
        }

        return accountRegex;
    }, []);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            let request = {
                amount,
                method,
                email,
                broker,
                provider: WithdrawProvider.PAYABL,
                account_number: accountNumber,
                first_name: firstName,
                last_name: lastName,
                phone,
            };
            switch (method) {
                case 'pix':
                    request = {
                        ...request,
                        ['document_type' as keyof typeof request]: documentType,
                        ['document_id' as keyof typeof request]: documentID,
                        ['account_type' as keyof typeof request]: accountType,
                    }
                    break;
                case 'oxxo':
                    request = {
                        ...request,
                        ['bank' as keyof typeof request]: bank,
                        ['national_id' as keyof typeof request]: nationalID,
                    }
                    break;
                case 'spei':
                    request = {
                        ...request,
                        ['document_type' as keyof typeof request]: documentType,
                        ['document_id' as keyof typeof request]: documentID,
                        ['account_type' as keyof typeof request]: accountType,
                        ['bank' as keyof typeof request]: bank,
                        ['national_id' as keyof typeof request]: nationalID,
                    }
                    break;
            }
            API.makePayablWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    ErrorHandler(e, `post${APILink.MAKE_PAYABL_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [firstName, lastName, method, validated, amount, accountNumber, email, phone, broker, documentID, documentType, bank, accountType, nationalID]);

    const submit = useCallback(() => {
        setInProgress(true);
        let inputs: Input[] = [
            {
                key: 'method',
                label: t('admin.withdraws.payment_method'),
                type: InputType.select,
                options: methods.map((i) => {
                    return {name: i.name.toString(), id: i.code ?? ''}
                })
            },
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.number, regex: emailRegex},
        ];
        let data = {
            method,
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            account_number: accountNumber
        };
        switch (method) {
            case 'pix':
                inputs = [
                    ...inputs,
                    {
                        key: 'account_number',
                        label: t('admin.withdraws.account_number'),
                        type: InputType.number,
                        regex: detectAccountRegex(accountType ?? '')
                    },
                    {key: 'document_type', label: t('admin.withdraws.document_type'), type: InputType.text},
                    {
                        key: 'document_id',
                        label: t('admin.withdraws.document_id'),
                        type: InputType.text,
                        regex: detectAccountRegex(documentType ?? '')
                    },
                    {key: 'account_type', label: t('admin.withdraws.account_type'), type: InputType.text},
                    {
                        key: 'phone',
                        label: t('admin.withdraws.phone'),
                        type: InputType.number,
                        regex: '^([+55]{3})(\\(?[0-9]{2}\\)?)? ?([0-9]{4,5})-?([0-9]{4})'
                    }
                ];
                data = {
                    ...data,
                    ['document_type' as keyof typeof data]: documentType,
                    ['document_id' as keyof typeof data]: documentID,
                    ['account_type' as keyof typeof data]: accountType,
                }
                break
            case 'oxxo':
                inputs = [
                    ...inputs,
                    {
                        key: 'account_number',
                        label: t('admin.withdraws.account_number'),
                        type: InputType.number,
                        regex: '^\\d{1,18}$'
                    },
                    {key: 'bank', label: t('admin.withdraws.bank'), type: InputType.number, regex: '^\\d{1,18}$'},
                    {
                        key: 'national_id',
                        label: t('admin.withdraws.national_id'),
                        type: InputType.number,
                        regex: '^\\d{1,18}$'
                    },
                    {
                        key: 'phone',
                        label: t('admin.withdraws.phone'),
                        type: InputType.number,
                        regex: '^([+]?52[ ,-]?1[ ,-])?[0-9]{2}[ ,-]?[0-9]{4}[ ,-]?[0-9]{4}$'
                    }
                ]
                data = {
                    ...data,
                    ['bank' as keyof typeof data]: bank,
                    ['national_id' as keyof typeof data]: nationalID,
                }
                break;
            case 'spei':
                inputs = [
                    ...inputs,
                    {key: 'document_type', label: t('admin.withdraws.document_type'), type: InputType.text},
                    {key: 'document_id', label: t('admin.withdraws.document_id'), type: InputType.text},
                    {key: 'account_type', label: t('admin.withdraws.account_type'), type: InputType.text},
                    {key: 'bank', label: t('admin.withdraws.bank'), type: InputType.number, regex: '^\\d{1,18}$'},
                    {
                        key: 'phone',
                        label: t('admin.withdraws.phone'),
                        type: InputType.number,
                        regex: '^([+]?52[ ,-]?1[ ,-])?[0-9]{2}[ ,-]?[0-9]{4}[ ,-]?[0-9]{4}$'
                    }
                ]
                if (documentType?.toLowerCase() === 'rfc') {
                    inputs.push({
                        key: 'national_id',
                        label: t('admin.withdraws.national_id'),
                        type: InputType.number,
                        regex: '^.{13}$'
                    })
                } else inputs.push({
                    key: 'national_id',
                    label: t('admin.withdraws.national_id'),
                    type: InputType.number,
                    regex: '^.{18}$'
                })
                if (accountType?.toLowerCase() === 'phone') {
                    inputs.push({
                        key: 'account_number',
                        label: t('admin.withdraws.account_number'),
                        type: InputType.number,
                        regex: '^\\d{10}$'
                    })
                } else if (accountType?.toLowerCase() === 'clabe') {
                    inputs.push({
                        key: 'account_number',
                        label: t('admin.withdraws.account_number'),
                        type: InputType.number,
                        regex: '^\\d{18}$'
                    })
                } else inputs.push({
                    key: 'account_number',
                    label: t('admin.withdraws.account_number'),
                    type: InputType.number,
                    regex: '^\\d{16}$'
                })
                data = {
                    ...data,
                    ['document_type' as keyof typeof data]: documentType,
                    ['document_id' as keyof typeof data]: documentID,
                    ['account_type' as keyof typeof data]: accountType,
                    ['bank' as keyof typeof data]: bank,
                    ['national_id' as keyof typeof data]: nationalID,
                }
                break;
        }
        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [amount, method, firstName, lastName, email, accountNumber, accountType, bank, nationalID, documentType, documentID, phone, callback]);

    const documentTypeUI = useMemo(() => {
        if (!method || method === 'oxxo')
            return <></>

        let types: FooterListItem[] = [];
        if (method === 'pix')
            types = [{name: 'CPF'}, {name: 'CNPJ'}];
        else
            types = [{name: 'RFC'}, {name: 'CURP'}];

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.document_type')} className="w-full min-w-[200px]"
                           selected={documentType}
                           onChange={changeDocumentType}
                           list={types}/>
        </div>
    }, [documentType, method, changeDocumentType]);

    const accountTypeUI = useMemo(() => {
        if (!method || method === 'oxxo')
            return <></>

        let types: FooterListItem[] = [];
        if (method === 'pix')
            types = [{name: 'CPF'}, {name: 'CNPJ'}, {name: 'EVP'}, {name: 'PHONE'}, {name: 'EMAIL'}];
        else
            types = [{name: 'Debit', code: 'debit'}, {name: 'Phone', code: 'phone'}, {name: 'Clabe', code: 'clabe'}];

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.account_type')} className="w-full min-w-[200px]"
                           selected={accountType}
                           onChange={changeAccountType}
                           list={types}/>
        </div>
    }, [method, accountType, changeAccountType]);

    const bankUI = useMemo(() => {
        if (!method || method === 'pix' || !banks)
            return <></>

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.bank')} className="w-full min-w-[200px]" selected={bank}
                           onChange={changeBank}
                           list={banks.mexicanbanks.map((i) => {
                               return {name: i.bankName, id: parseInt(i.id)};
                           })}/>
        </div>
    }, [banks, method, bank, changeBank]);

    const documentIDUI = useMemo(() => {
        if (!method || method === 'oxxo')
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.document_id')} type="text" validated={validated} value={documentID}
                          fieldName="document_id"
                          onChange={changeDocumentID}/>
        </div>
    }, [method, validated, documentID, changeDocumentID])

    const nationalIDUI = useMemo(() => {
        if (!method || method === 'pix')
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.national_id')} type="text" validated={validated} value={nationalID}
                          fieldName="national_id"
                          onChange={changeNationalID}/>
        </div>
    }, [method, accountNumber, validated, nationalID, changeNationalID])

    const amountUI = useMemo(() => {
        if (!method)
            return <></>


        const allowedMethod = allowedMethodData.find(a => a.code === method);
        if (!allowedMethod || allowedMethod.currencies.length === 0)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency: allowedMethod.currencies[0]})} type="number"
                          validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [validated, method, changeAmount, amount]);

    const firstNameUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [method, accountNumber, validated, firstName, changeFirstName, nationalID])

    const lastNameUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [method, validated, lastName, changeLastName])

    const emailUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [method, validated, email, changeEmail])

    const phoneUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.phone')} type="phone" validated={validated} value={phone}
                          fieldName="phone"
                          onChange={changePhone}/>
        </div>
    }, [method, validated, phone, changePhone])

    const accountUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_number')} type="text" validated={validated}
                          value={accountNumber}
                          fieldName="account_number"
                          onChange={changeAccountNumber}/>
        </div>
    }, [method, validated, accountNumber, changeAccountNumber])

    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        clearData();
        getBanks();
        if (clientTransactionID) {
            getClientTransaction();
        }
    }, [broker, clientTransactionID]);

    return <>
        <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.payment_method')} className="w-full min-w-[200px]" selected={method}
                           onChange={changeMethod}
                           list={methods}/>
        </div>
        {documentTypeUI}
        {documentIDUI}
        {accountTypeUI}
        {bankUI}
        {accountUI}
        {nationalIDUI}
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {phoneUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(PayablForm);
